<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Year pickers header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="5" md="3">
            <v-select
              outlined dense hide-details
              :label="$t('$vuetify.usporedbaGodina.olderYear')"
              v-model="olderYear"
              :items="availableYears"
              class="older-year-select"
              @change="fetchBothYears"
            />
          </v-col>
          <v-col cols="12" sm="5" md="3">
            <v-select
              outlined dense hide-details
              :label="$t('$vuetify.usporedbaGodina.currentYear')"
              v-model="currentYear"
              :items="availableYears"
              class="current-year-select"
              @change="fetchBothYears"
            />
          </v-col>
          <v-col cols="auto" class="d-flex align-center ml-auto" style="gap: 4px;">
            <v-switch
              v-model="filterPaid"
              dense hide-details
              color="orange darken-1"
              class="mt-0 pt-0 mr-2"
              label="Samo plaćeni"
              @change="fetchBothYears"
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="#9C27B0" size="48" />
        <div class="mt-4 grey--text">{{ $t('$vuetify.usporedbaGodina.loading') }}</div>
      </div>

      <template v-else>

        <!-- Section A: Year totals — horizontal bar chart -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">{{ $t('$vuetify.usporedbaGodina.totalComparison') }}</div>
          <apexchart
            type="bar"
            height="150"
            :options="totalChartOptions"
            :series="totalSeries"
          />
        </v-card>

        <!-- Section B: Monthly grouped bar chart -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">{{ $t('$vuetify.usporedbaGodina.monthlyChart') }}</div>
          <apexchart
            type="bar"
            height="300"
            :options="monthlyChartOptions"
            :series="monthlySeries"
          />
        </v-card>

        <!-- Section C: Monthly table -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">{{ $t('$vuetify.usporedbaGodina.monthlyTable') }}</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ $t('$vuetify.usporedbaGodina.month') }}</th>
                <th class="text-right" style="color: #9C27B0;">{{ olderYear }}</th>
                <th class="text-right" style="color: #00838F;">{{ currentYear }}</th>
                <th class="text-right">Δ%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in monthlyTableRows" :key="i">
                <td>{{ row.monthName }}</td>
                <td class="text-right">{{ row.olderFormatted }}</td>
                <td class="text-right">{{ row.currentFormatted }}</td>
                <td class="text-right">
                  <span
                    v-if="row.delta !== null"
                    :style="{ color: row.delta >= 0 ? '#4CAF50' : '#F44336', fontWeight: '600' }"
                  >
                    <v-icon x-small :color="row.delta >= 0 ? '#4CAF50' : '#F44336'">
                      {{ row.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    {{ Math.abs(row.delta).toFixed(1) }}%
                  </span>
                  <span v-else style="color: #9CA3AF;">—</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background: #F8FAFC; border-top: 2px solid #E0E6ED;">
                <td><strong>{{ $t('$vuetify.usporedbaGodina.total') }}</strong></td>
                <td class="text-right"><strong>{{ formatEur(olderTotalEur) }}</strong></td>
                <td class="text-right"><strong>{{ formatEur(currentTotalEur) }}</strong></td>
                <td class="text-right">
                  <span
                    v-if="olderTotalEur > 0 && currentTotalEur > 0"
                    :style="{ color: totalDelta >= 0 ? '#4CAF50' : '#F44336', fontWeight: '600' }"
                  >
                    <v-icon x-small :color="totalDelta >= 0 ? '#4CAF50' : '#F44336'">
                      {{ totalDelta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    {{ Math.abs(totalDelta).toFixed(1) }}%
                  </span>
                  <span v-else style="color: #9CA3AF;">—</span>
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const MONTH_ABBR = ['Sij', 'Velj', 'Ožu', 'Tra', 'Svi', 'Lip', 'Srp', 'Kol', 'Ruj', 'Lis', 'Stu', 'Pro']
const COLOR_OLDER = '#9C27B0'
const COLOR_CURRENT = '#00BCD4'

export default {
  name: 'UsporedbaGodina',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    const now = new Date().getFullYear()
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      availableYears: Array.from({ length: 12 }, (_, i) => now + 1 - i),
      olderYear: now - 1,
      currentYear: now,
      filterPaid: false,
      monthDataOlder: Array(12).fill(null),
      monthDataCurrent: Array(12).fill(null)
    }
  },

  computed: {
    olderTotalEur () {
      return this.monthDataOlder.reduce((acc, v) => acc + (v || 0), 0) / 100
    },
    currentTotalEur () {
      return this.monthDataCurrent.reduce((acc, v) => acc + (v || 0), 0) / 100
    },
    totalDelta () {
      if (!this.olderTotalEur) return 0
      return (this.currentTotalEur - this.olderTotalEur) / this.olderTotalEur * 100
    },

    totalChartOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: true } },
        colors: [COLOR_OLDER, COLOR_CURRENT],
        xaxis: { categories: [String(this.olderYear), String(this.currentYear)] },
        dataLabels: { enabled: true, formatter: v => this.formatEur(v) },
        legend: { show: false },
        tooltip: { y: { formatter: v => this.formatEur(v) } },
        yaxis: { labels: { style: { fontFamily: 'Raleway, sans-serif' } } }
      }
    },
    totalSeries () {
      return [{ name: this.$t('$vuetify.usporedbaGodina.revenue'), data: [this.olderTotalEur, this.currentTotalEur] }]
    },

    monthlyChartOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Raleway, sans-serif' },
        plotOptions: { bar: { horizontal: false, columnWidth: '70%' } },
        colors: [COLOR_OLDER, COLOR_CURRENT],
        xaxis: { categories: MONTH_ABBR },
        yaxis: { labels: { formatter: v => this.formatEur(v) } },
        dataLabels: { enabled: false },
        legend: { show: true },
        tooltip: { y: { formatter: v => this.formatEur(v) } }
      }
    },
    monthlySeries () {
      return [
        { name: String(this.olderYear), data: this.monthDataOlder.map(v => parseFloat(((v || 0) / 100).toFixed(2))) },
        { name: String(this.currentYear), data: this.monthDataCurrent.map(v => parseFloat(((v || 0) / 100).toFixed(2))) }
      ]
    },

    monthlyTableRows () {
      return MONTH_ABBR.map((name, i) => {
        const older = this.monthDataOlder[i]
        const current = this.monthDataCurrent[i]
        const delta = (older !== null && older > 0 && current !== null)
          ? (current - older) / older * 100
          : null
        return {
          monthName: name,
          olderFormatted: older ? this.formatEur(older / 100) : '—',
          currentFormatted: current ? this.formatEur(current / 100) : '—',
          delta
        }
      })
    }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchBothYears()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (valueEur) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(valueEur)
    },

    async fetchYearData (year) {
      const companyId = state.getCurrentCompany().id
      const db = getFirestore()
      const col = collection(db, `receipts/${companyId}/receipts`)
      const sf = this.filterPaid ? [where('payment.current_status', 'in', ['PAID', 'REVERSED'])] : []

      const results = await Promise.all(
        Array.from({ length: 12 }, (_, i) => {
          const monthStart = Math.floor(new Date(year, i, 1).getTime() / 1000)
          const monthEnd = Math.floor(new Date(year, i + 1, 1).getTime() / 1000)
          return getAggregateFromServer(
            query(col, where('issue_date_time', '>=', monthStart), where('issue_date_time', '<', monthEnd), ...sf),
            { totalCents: sum('total') }
          )
        })
      )

      return results.map(snap => {
        const val = snap.data().totalCents
        return (val !== null && val > 0) ? val : null
      })
    },

    async fetchBothYears () {
      this.loading = true
      try {
        const [older, current] = await Promise.all([
          this.fetchYearData(this.olderYear),
          this.fetchYearData(this.currentYear)
        ])
        this.monthDataOlder = older
        this.monthDataCurrent = current
      } catch (err) {
        console.error('UsporedbaGodina:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.section-title { font-size: 16px; font-weight: 700; color: #2d3748; }
.older-year-select >>> fieldset { border-color: rgba(156, 39, 176, 0.5) !important; }
.older-year-select >>> .v-select__selection { color: #9C27B0 !important; font-weight: 600 !important; }
.current-year-select >>> fieldset { border-color: rgba(0, 188, 212, 0.5) !important; }
.current-year-select >>> .v-select__selection { color: #00838F !important; font-weight: 600 !important; }
</style>
